import { getSpeedColor, percentile, clamp } from "./utils.js";

const TRACK_WEIGHT = 4;
const DEFAULT_TRACK_COLOR = "#dc2626";
const googleReadyCallbacks = [];

window.bhGpxViewerGoogleReady = function () {
  googleReadyCallbacks.splice(0).forEach((callback) => callback());
};

export async function createMapProvider(mapEl, root) {
  if (root.dataset.mapProvider === "google") {
    await waitForGoogleMaps(root);
    return createGoogleProvider(mapEl);
  }

  if (!window.L) {
    throw new Error("Leaflet failed to load.");
  }

  return createLeafletProvider(mapEl, {
    thunderforestUrl: root.dataset.thunderforestUrl,
  });
}

function createLeafletProvider(mapEl, options = {}) {
  const map = L.map(mapEl, {
    zoomControl: true,
    preferCanvas: true,
    scrollWheelZoom: false,
  }).setView([39.8283, -98.5795], 4);

  const baseMaps = {};
  if (options.thunderforestUrl) {
    baseMaps.Terrain = L.tileLayer(options.thunderforestUrl, {
      maxZoom: 24,
      maxNativeZoom: 22,
      crossOrigin: "anonymous",
      attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | Map style: &copy; <a href="https://www.thunderforest.com/">Thunderforest</a>',
    });
  }

  Object.assign(baseMaps, {
    Topo: L.tileLayer(
      "https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png",
      {
        maxZoom: 20,
        crossOrigin: "anonymous",
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, CyclOSM',
      }
    ),
    "OSM Standard": L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 19,
        crossOrigin: "anonymous",
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }
    ),
    "Satellite (ESRI)": L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 19,
        attribution: "Tiles &copy; Esri",
      }
    ),
    "Topo (ESRI)": L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 19,
        attribution: "Tiles &copy; Esri",
      }
    ),
  });

  (baseMaps.Terrain || baseMaps.Topo).addTo(map);
  L.control
    .layers(baseMaps, null, { collapsed: false, position: "topright" })
    .addTo(map);
  L.control.scale({ metric: true, imperial: true, position: "bottomleft" }).addTo(map);

  setTimeout(() => map.invalidateSize(), 50);
  window.addEventListener("resize", () => map.invalidateSize());

  return {
    addTrack(profile, handlers) {
      const layer = createLeafletTrackLayer(map, profile, handlers).addTo(map);
      return layer;
    },
    removeLayer(layer) {
      map.removeLayer(layer);
    },
    getBounds(layer) {
      return layer.getBounds();
    },
    async fitBounds(bounds) {
      if (bounds.isValid()) {
        await fitLeafletBoundsAndWait(map, bounds, { padding: [20, 20] });
      }
    },
    createHighlight(split) {
      const layer = createLeafletHighlightLayer(split).addTo(map);
      layer.bringToFront();
      return layer;
    },
    showCursor(point) {
      if (!this.cursorMarker) {
        this.cursorMarker = L.circleMarker([0, 0], {
          radius: 7,
          color: "#111827",
          fillColor: "#ffffff",
          fillOpacity: 1,
          interactive: false,
          opacity: 1,
          pane: "markerPane",
          weight: 3,
        });
      }

      this.cursorMarker.setLatLng([point.latitude, point.longitude]);
      if (!map.hasLayer(this.cursorMarker)) {
        this.cursorMarker.addTo(map);
      }
    },
    clearCursor() {
      if (this.cursorMarker && map.hasLayer(this.cursorMarker)) {
        map.removeLayer(this.cursorMarker);
      }
    },
  };
}

function createLeafletTrackLayer(map, profile, handlers) {
  const speeds = profile.segments.map((segment) => segment.speed).filter(Number.isFinite);
  const minSpeed = percentile(speeds, 0.05);
  const maxSpeed = percentile(speeds, 0.95);
  const layer = L.featureGroup();

  profile.segments.forEach((segment) => {
    const polyline = L.polyline(
      [
        [segment.from.latitude, segment.from.longitude],
        [segment.to.latitude, segment.to.longitude],
      ],
      {
        color: getSegmentColor(segment, profile, minSpeed, maxSpeed),
        lineCap: "round",
        lineJoin: "round",
        opacity: 0.95,
        smoothFactor: 1,
        weight: TRACK_WEIGHT,
      }
    );

    polyline.on("mousemove", (event) => {
      handlers.onHover(distanceFromLeafletLatLng(map, segment, event.latlng));
    });
    polyline.on("mouseover", () => {
      handlers.onHover((segment.startDistance + segment.endDistance) / 2);
    });
    polyline.on("mouseout", handlers.onLeave);
    polyline.addTo(layer);
  });

  return layer;
}

function createLeafletHighlightLayer(split) {
  const layer = L.featureGroup();

  split.pathSegments.forEach((pathSegment) => {
    L.polyline(pathSegment, {
      color: "#111827",
      interactive: false,
      lineCap: "round",
      lineJoin: "round",
      opacity: 0.9,
      weight: 10,
    }).addTo(layer);
    L.polyline(pathSegment, {
      color: "#ffffff",
      interactive: false,
      lineCap: "round",
      lineJoin: "round",
      opacity: 0.95,
      weight: 4,
    }).addTo(layer);
  });

  return layer;
}

function createGoogleProvider(mapEl) {
  const map = new google.maps.Map(mapEl, {
    center: { lat: 39.8283, lng: -98.5795 },
    disableDefaultUI: true,
    fullscreenControl: true,
    gestureHandling: "cooperative",
    mapTypeId: "terrain",
    scaleControl: true,
    zoom: 4,
    zoomControl: true,
  });

  return {
    addTrack(profile, handlers) {
      return createGoogleTrackLayer(map, profile, handlers);
    },
    removeLayer(layer) {
      layer.forEach((item) => item.setMap(null));
    },
    getBounds(layer) {
      return layer.bounds;
    },
    async fitBounds(bounds) {
      if (!bounds || bounds.isEmpty()) {
        return;
      }

      await fitGoogleBoundsAndWait(map, bounds);
    },
    createHighlight(split) {
      return createGoogleHighlightLayer(map, split);
    },
    showCursor(point) {
      if (!this.cursorMarker) {
        this.cursorMarker = new google.maps.Marker({
          clickable: false,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: "#ffffff",
            fillOpacity: 1,
            scale: 7,
            strokeColor: "#111827",
            strokeWeight: 3,
          },
          map,
          zIndex: 1000,
        });
      }

      this.cursorMarker.setPosition({ lat: point.latitude, lng: point.longitude });
      this.cursorMarker.setMap(map);
    },
    clearCursor() {
      if (this.cursorMarker) {
        this.cursorMarker.setMap(null);
      }
    },
  };
}

function createGoogleTrackLayer(map, profile, handlers) {
  const speeds = profile.segments.map((segment) => segment.speed).filter(Number.isFinite);
  const minSpeed = percentile(speeds, 0.05);
  const maxSpeed = percentile(speeds, 0.95);
  const bounds = new google.maps.LatLngBounds();
  const polylines = [];

  profile.segments.forEach((segment) => {
    const path = [
      { lat: segment.from.latitude, lng: segment.from.longitude },
      { lat: segment.to.latitude, lng: segment.to.longitude },
    ];
    const polyline = new google.maps.Polyline({
      clickable: true,
      geodesic: true,
      map,
      path,
      strokeColor: getSegmentColor(segment, profile, minSpeed, maxSpeed),
      strokeOpacity: 0.95,
      strokeWeight: TRACK_WEIGHT,
      zIndex: 1,
    });

    polyline.addListener("mousemove", (event) => {
      handlers.onHover(distanceFromGoogleLatLng(segment, event.latLng));
    });
    polyline.addListener("mouseover", () => {
      handlers.onHover((segment.startDistance + segment.endDistance) / 2);
    });
    polyline.addListener("mouseout", handlers.onLeave);
    bounds.extend(path[0]);
    bounds.extend(path[1]);
    polylines.push(polyline);
  });

  polylines.bounds = bounds;
  return polylines;
}

function getSegmentColor(segment, profile, minSpeed, maxSpeed) {
  if (!profile.hasTime || !Number.isFinite(segment.speed)) {
    return DEFAULT_TRACK_COLOR;
  }

  return getSpeedColor(segment.speed, minSpeed, maxSpeed);
}

function createGoogleHighlightLayer(map, split) {
  const polylines = [];

  split.pathSegments.forEach((pathSegment) => {
    const path = pathSegment.map(([latitude, longitude]) => ({ lat: latitude, lng: longitude }));
    polylines.push(new google.maps.Polyline({
      clickable: false,
      geodesic: true,
      map,
      path,
      strokeColor: "#111827",
      strokeOpacity: 0.9,
      strokeWeight: 10,
      zIndex: 20,
    }));
    polylines.push(new google.maps.Polyline({
      clickable: false,
      geodesic: true,
      map,
      path,
      strokeColor: "#ffffff",
      strokeOpacity: 0.95,
      strokeWeight: 4,
      zIndex: 21,
    }));
  });

  return polylines;
}

function waitForGoogleMaps(root) {
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  if (root.dataset.hasGoogleKey !== "true") {
    return Promise.reject(new Error("Google Maps API key is missing."));
  }

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new Error("Google Maps API failed to load."));
    }, 10000);

    googleReadyCallbacks.push(() => {
      window.clearTimeout(timeout);
      resolve();
    });
  });
}

function fitLeafletBoundsAndWait(map, bounds, options) {
  return new Promise((resolve) => {
    let finished = false;
    let timeout = null;
    const finish = () => {
      if (finished) {
        return;
      }

      finished = true;
      clearTimeout(timeout);
      map.off("moveend", finish);
      resolve();
    };

    map.on("moveend", finish);
    map.fitBounds(bounds, options);
    timeout = setTimeout(finish, 500);
  });
}

function fitGoogleBoundsAndWait(map, bounds) {
  return new Promise((resolve) => {
    const listener = google.maps.event.addListenerOnce(map, "idle", resolve);
    map.fitBounds(bounds, 20);
    setTimeout(() => {
      google.maps.event.removeListener(listener);
      resolve();
    }, 500);
  });
}

function distanceFromLeafletLatLng(map, segment, latlng) {
  const from = map.latLngToLayerPoint([segment.from.latitude, segment.from.longitude]);
  const to = map.latLngToLayerPoint([segment.to.latitude, segment.to.longitude]);
  const pointer = map.latLngToLayerPoint(latlng);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;

  if (!lengthSquared) {
    return segment.startDistance;
  }

  const ratio = clamp(((pointer.x - from.x) * dx + (pointer.y - from.y) * dy) / lengthSquared, 0, 1);
  return segment.startDistance + ratio * segment.distance;
}

function distanceFromGoogleLatLng(segment, latLng) {
  if (!latLng) {
    return (segment.startDistance + segment.endDistance) / 2;
  }

  const latitude = typeof latLng.lat === "function" ? latLng.lat() : latLng.lat;
  const longitude = typeof latLng.lng === "function" ? latLng.lng() : latLng.lng;
  const midLatitude = (segment.from.latitude + segment.to.latitude) / 2;
  const scale = Math.cos(midLatitude * Math.PI / 180);
  const from = { x: segment.from.longitude * scale, y: segment.from.latitude };
  const to = { x: segment.to.longitude * scale, y: segment.to.latitude };
  const pointer = { x: longitude * scale, y: latitude };
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;

  if (!lengthSquared) {
    return segment.startDistance;
  }

  const ratio = clamp(((pointer.x - from.x) * dx + (pointer.y - from.y) * dy) / lengthSquared, 0, 1);
  return segment.startDistance + ratio * segment.distance;
}
